import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ImageUp, RotateCcw, Save } from "lucide-react";
import { TOKEN_KEY } from "@/pages/AdminLogin";
import { SITE } from "@/lib/site-config";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Each slot is clearly assigned to its website section so Tobias knows which image he replaces.
const SECTIONS = [
  {
    title: "Logo (Kopf- & Fußzeile)",
    dark: true,
    items: [{ key: "logo", name: "Logo", def: "/brand/logo-white-1200.png" }],
  },
  {
    title: "Hero (Startbereich)",
    dark: true,
    items: [{ key: "hero", name: "Hintergrundbild", def: SITE.images.hero }],
  },
  {
    title: "Über Tobias",
    items: [{ key: "about", name: "Profilbild Tobias", def: SITE.images.about }],
  },
];

const AdminSiteImages = () => {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const api = useMemo(
    () => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 30000 }),
    [token]
  );

  const [overrides, setOverrides] = useState({});
  const [titles, setTitles] = useState({ ueber_eyebrow: "", ueber_title: "" });
  const [loading, setLoading] = useState(true);

  const handleAuthErr = useCallback((e) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 403 && detail === "PASSWORD_CHANGE_REQUIRED") { navigate("/admin/leads", { replace: true }); return true; }
    if ([401, 403].includes(status)) { localStorage.removeItem(TOKEN_KEY); navigate("/admin/login", { replace: true }); return true; }
    return false;
  }, [navigate]);

  const load = useCallback(async () => {
    if (!token) { navigate("/admin/login", { replace: true }); return; }
    setLoading(true);
    try {
      const r = await api.get("/admin/site-content");
      const imgs = {};
      Object.entries(r.data.images || {}).forEach(([k, v]) => {
        imgs[k] = typeof v === "string" && v.startsWith("http") ? v : `${BACKEND_URL}${v}`;
      });
      setOverrides(imgs);
      setTitles({
        ueber_eyebrow: r.data.titles?.ueber_eyebrow ?? "",
        ueber_title: r.data.titles?.ueber_title ?? "",
      });
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Laden fehlgeschlagen.");
    } finally { setLoading(false); }
  }, [api, token, navigate, handleAuthErr]);

  useEffect(() => { document.title = "CRM · Website-Bilder"; load(); }, []); // eslint-disable-line

  const saveTitles = async () => {
    try {
      await api.patch("/admin/site-content/titles", {
        ueber_eyebrow: titles.ueber_eyebrow,
        ueber_title: titles.ueber_title,
      });
      toast.success("Sektionstitel gespeichert.");
    } catch (e) { if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
  };

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-site-images-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin/leads" className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-2">
          <ArrowLeft size={12} /> CRM
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Website-Bilder</h1>
        <p className="text-[#A8A58F] text-sm mt-1 mb-8">
          Ersetze bestehende Bilder der Website. Ohne Ersatz bleibt jeweils das aktuelle Bild sichtbar. Größe, Position und Layout bleiben unverändert.
        </p>

        {loading ? (
          <div className="text-[#C9C6B4] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Lade…</div>
        ) : (
          <div className="space-y-10">
            {/* Editable section title */}
            <div className="glass-strong rounded-2xl p-5 sm:p-6" data-testid="section-title-editor">
              <h2 className="font-display text-xl font-black mb-1">Sektionstitel · „Einblick über meinen Weg"</h2>
              <p className="text-[#A8A58F] text-sm mb-4">Titel des Video-Abschnitts „Über meinen Weg". Schriftart, Größe und Position bleiben unverändert.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#C9C6B4] mb-1 block">Eyebrow (kleine Zeile)</label>
                  <input value={titles.ueber_eyebrow} onChange={(e) => setTitles((t) => ({ ...t, ueber_eyebrow: e.target.value }))}
                    className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]" data-testid="title-eyebrow-input" />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#C9C6B4] mb-1 block">Titel (große Zeile)</label>
                  <input value={titles.ueber_title} onChange={(e) => setTitles((t) => ({ ...t, ueber_title: e.target.value }))}
                    className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]" data-testid="title-title-input" />
                </div>
              </div>
              <button onClick={saveTitles} className="mt-4 inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-full px-5 py-2.5 text-sm" data-testid="save-titles-btn">
                <Save size={14} /> Titel speichern
              </button>
            </div>

            {/* Image slots grouped by section */}
            {SECTIONS.map((section) => (
              <div key={section.title}>
                <h2 className="font-display text-lg font-black mb-3">{section.title}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {section.items.map((item) => (
                    <SlotCard
                      key={item.key}
                      item={item}
                      dark={section.dark}
                      currentUrl={overrides[item.key] || item.def}
                      hasOverride={!!overrides[item.key]}
                      api={api}
                      onDone={load}
                      handleAuthErr={handleAuthErr}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SlotCard = ({ item, dark, currentUrl, hasOverride, api, onDone, handleAuthErr }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Nur JPG, PNG oder WEBP erlaubt."); return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/site-content/image/${item.key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bild ersetzt.");
      setFile(null); setPreview(null);
      onDone();
    } catch (e) { if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen."); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Auf Originalbild zurücksetzen?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/site-content/image/${item.key}`);
      toast.success("Auf Original zurückgesetzt.");
      onDone();
    } catch (e) { if (!handleAuthErr(e)) toast.error("Zurücksetzen fehlgeschlagen."); }
    finally { setBusy(false); }
  };

  const shown = preview || currentUrl;

  return (
    <div className="glass-strong rounded-2xl p-4 flex flex-col" data-testid={`slot-${item.key}`}>
      <div className={`relative rounded-xl overflow-hidden mb-3 aspect-[4/3] flex items-center justify-center ${dark ? "bg-[#3A4230]" : "bg-[#4E5A3B]/40"}`}>
        {shown ? (
          <img src={shown} alt={item.name} className="w-full h-full object-contain" data-testid={`slot-preview-${item.key}`} />
        ) : (
          <span className="text-[#A8A58F] text-xs">Kein Bild</span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-bold text-sm text-[#F3F1E8]">{item.name}</div>
        <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${hasOverride ? "text-[#B8CFA0] border-[#B8CFA0]/40" : "text-[#A8A58F] border-white/15"}`}>
          {hasOverride ? "Ersetzt" : "Original"}
        </span>
      </div>
      <div className="mt-auto space-y-2">
        <label className="flex items-center justify-center gap-2 cursor-pointer border border-white/15 hover:bg-black/[0.15] text-sm font-bold rounded-lg px-3 py-2 transition-colors" data-testid={`replace-btn-${item.key}`}>
          <ImageUp size={14} /> Bild ersetzen
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPick} data-testid={`file-input-${item.key}`} />
        </label>
        {file && (
          <button onClick={save} disabled={busy} className="w-full flex items-center justify-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] disabled:opacity-50 text-[#3A4230] font-extrabold uppercase tracking-wider rounded-lg px-3 py-2 text-sm" data-testid={`save-btn-${item.key}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
          </button>
        )}
        {hasOverride && !file && (
          <button onClick={reset} disabled={busy} className="w-full flex items-center justify-center gap-2 border border-white/12 hover:bg-black/[0.15] text-[#C9C6B4] text-xs font-bold rounded-lg px-3 py-2" data-testid={`reset-btn-${item.key}`}>
            <RotateCcw size={12} /> Auf Original zurücksetzen
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminSiteImages;
